import { z } from 'zod';
import { prefixed, zI18nKey, zItemId, zNpcId, zThemeTag, zWallFloorId } from './ids';
import { zCondition } from './progression';

/**
 * Studio NANO shoots (studio-nano.md §4.2). Every criterion counts what stands in the studio right now, so
 * the player builds the set from things they own and the checklist ticks itself.
 */
export const zShootCriterion = z.discriminatedUnion('type', [
  /** At least `count` placed things with this theme tag. */
  z.object({ type: z.literal('theme'), tag: zThemeTag, count: z.number().int().min(1).max(4) }),
  /** At least `count` placed things with this item tag (seat, light, plant…). */
  z.object({ type: z.literal('tag'), tag: z.string().min(1), count: z.number().int().min(1).max(4) }),
  /** This exact item is in the studio. */
  z.object({ type: z.literal('item'), itemId: zItemId }),
  /** The studio wall or floor is one of these. */
  z.object({ type: z.literal('wallFloor'), ids: z.array(zWallFloorId).min(1).max(4) }),
  /** The studio's coziness points (economy §6 formula). */
  z.object({ type: z.literal('cozy'), points: z.number().int().positive() }),
]);
export type ShootCriterion = z.output<typeof zShootCriterion>;

export const zShootId = prefixed('shoot_');
export type ShootId = z.output<typeof zShootId>;

export const zStudioShootDef = z.object({
  id: zShootId,
  /** Who books the shoot (and gets the friendship). */
  npcId: zNpcId,
  /** Who stands on the pose mark, when not the booker (Bé Mít books Sếp Mèo). */
  model: zNpcId.optional(),
  briefKey: zI18nKey,
  criteria: z.array(zShootCriterion).min(1).max(3),
  rewardCoins: z.number().int().positive().optional(),
  weight: z.number().positive().default(1),
  /** The neighbour's first booking, always offered before any other of theirs. */
  isFirst: z.boolean().default(false),
  conditions: z.array(zCondition).default([]),
});
export type StudioShootDefInput = z.input<typeof zStudioShootDef>;
export type StudioShootDef = z.output<typeof zStudioShootDef>;
